/** The console's own addresses: `/console`, `/console/<screen>` and `/console/<screen>/<item>`. */

export type Screen = 'overview' | 'project' | 'themes' | 'layers' | 'widgets' | 'brand' | 'history';

export interface Route {
  screen: Screen;
  item?: string;
}

export const BASE = '/console';

export const SCREENS: readonly { id: Screen; label: string; hint: string }[] = [
  { id: 'overview', label: 'Overview', hint: 'Where the settings are kept and what is live' },
  { id: 'project', label: 'Project', hint: 'Name, sign-in, export coordinate systems' },
  { id: 'themes', label: 'Themes', hint: 'Theme pages and their layer lists' },
  { id: 'layers', label: 'Layers', hint: 'Sources, fields, symbology, popups' },
  { id: 'widgets', label: 'Tools', hint: 'Settings for each tool' },
  { id: 'brand', label: 'Brand', hint: 'Logo, banner, theme icons' },
  { id: 'history', label: 'History', hint: 'Every saved change, and undo' },
];

const IDS = new Set<string>(SCREENS.map((s) => s.id));
const ITEM = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/;

export function isConsolePath(pathname: string): boolean {
  return pathname === BASE || pathname.startsWith(`${BASE}/`);
}

export function parseRoute(pathname: string): Route {
  const parts = pathname.replace(/\/+$/, '').slice(BASE.length).split('/').filter(Boolean);
  const [screen, item] = parts.map((p) => {
    try {
      return decodeURIComponent(p);
    } catch {
      return '';
    }
  });
  if (!screen || !IDS.has(screen)) return { screen: 'overview' };
  return item && ITEM.test(item) ? { screen: screen as Screen, item } : { screen: screen as Screen };
}

export function routePath(route: Route): string {
  if (route.screen === 'overview') return BASE;
  return `${BASE}/${route.screen}${route.item ? `/${encodeURIComponent(route.item)}` : ''}`;
}

/** The settings reference page for a screen: the first of its candidates the server has. */
export function docPageFor(route: Route, pages: readonly string[]): string | null {
  const candidates: Record<Screen, string[]> = {
    overview: ['CONFIGURATION.md', 'config/README.md', 'config/index.md'],
    project: ['config/project.md'],
    themes: ['config/theme.md', 'config/themes.md'],
    layers: ['config/layer.md', 'config/layers.md'],
    widgets: route.item
      ? [
          `config/widgets/${route.item}.md`,
          `config/widget-${route.item}.md`,
          `config/${route.item}.md`,
          'config/widgets.md',
        ]
      : ['config/widgets.md', 'config/widgets/index.md', 'config/index.md'],
    brand: ['config/brand.md', 'config/project.md'],
    history: ['CONSOLE.md'],
  };
  return candidates[route.screen].find((c) => pages.includes(c)) ?? null;
}

/** An id for a new theme or layer: lower-case letters, digits and dashes (the pack's Slug rule). */
export const SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function slugProblem(id: string, taken: readonly string[]): string {
  if (!id) return 'Give it an id.';
  if (!SLUG.test(id)) return 'Use 2 to 63 lower-case letters, digits and dashes, starting with a letter or digit.';
  if (taken.includes(id)) return `${id} is already taken.`;
  return '';
}
